// ** react imports
import { useState, useMemo, useEffect } from 'react';

// ** api methods for document management
import {
  useUploadTaxFileDocumentMutation,
  useUploadQuizResponseDocumentMutation,
  useGetDocumentDownloadUrlMutation,
  useDeleteDocumentMutation,
  useGetUserDocumentsQuery,
  useGetTaxFileDocumentsQuery,
  useUpdateTaxFileDocumentsMutation,
} from '../../../redux/slices/documents/slice';

export const useUploadTaxFileDocument = () => {
  try {
    // api call mutation
    const [
      uploadTaxFileDocument,
      {
        isLoading: isUploadTaxFileDocumentLoading,
        isError: isUploadTaxFileDocumentError,
        error: uploadTaxFileDocumentError,
        isSuccess: isUploadTaxFileDocumentSuccess,
        isFetching: isUploadTaxFileDocumentFetching,
      },
    ] = useUploadTaxFileDocumentMutation();

    // handlers
    const handleUploadTaxFileDocument = async data => {
      return await uploadTaxFileDocument(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUploadTaxFileDocumentLoading,
        isUploadTaxFileDocumentFetching,
        isUploadTaxFileDocumentError,
        uploadTaxFileDocumentError,
        isUploadTaxFileDocumentSuccess,
        uploadTaxFileDocument: handleUploadTaxFileDocument,
      }),
      [
        isUploadTaxFileDocumentLoading,
        isUploadTaxFileDocumentFetching,
        isUploadTaxFileDocumentError,
        uploadTaxFileDocumentError,
        isUploadTaxFileDocumentSuccess,
        handleUploadTaxFileDocument,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error uploading tax file document:', error);
    throw error;
  }
};

export const useUploadQuizResponseDocument = () => {
  try {
    // api call mutation
    const [
      uploadQuizResponseDocument,
      {
        isLoading: isUploadQuizResponseDocumentLoading,
        isError: isUploadQuizResponseDocumentError,
        error: uploadQuizResponseDocumentError,
        isSuccess: isUploadQuizResponseDocumentSuccess,
      },
    ] = useUploadQuizResponseDocumentMutation();

    // handlers
    const handleUploadQuizResponseDocument = async (
      file,
      quizResponseId,
      questionId,
      onUploadProgress
    ) => {
      // Validate inputs
      if (!file) {
        throw new Error('File is required');
      }
      if (!quizResponseId) {
        throw new Error('Quiz Response ID is required');
      }
      if (!questionId) {
        throw new Error('Question ID is required');
      }

      // Create FormData
      const formData = new FormData();
      formData.append('document', file);

      // Pass onUploadProgress to the mutation if it exists
      return await uploadQuizResponseDocument({
        formData,
        quizResponseId,
        questionId,
        onUploadProgress,
      });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUploadQuizResponseDocumentLoading,
        isUploadQuizResponseDocumentError,
        uploadQuizResponseDocumentError,
        isUploadQuizResponseDocumentSuccess,
        uploadQuizResponseDocument: handleUploadQuizResponseDocument,
      }),
      [
        isUploadQuizResponseDocumentLoading,
        isUploadQuizResponseDocumentError,
        uploadQuizResponseDocumentError,
        isUploadQuizResponseDocumentSuccess,
        handleUploadQuizResponseDocument,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error uploading quiz response document:', error);
    throw error;
  }
};

export const useGetDocumentDownloadUrl = () => {
  try {
    // api call mutation
    const [
      getDocumentDownloadUrl,
      {
        isLoading: isGetDocumentDownloadUrlLoading,
        isError: isGetDocumentDownloadUrlError,
        error: getDocumentDownloadUrlError,
        isSuccess: isGetDocumentDownloadUrlSuccess,
        isFetching: isGetDocumentDownloadUrlFetching,
      },
    ] = useGetDocumentDownloadUrlMutation();

    // handlers
    const handleGetDocumentDownloadUrl = async documentId => {
      if (!documentId) {
        throw new Error('Document ID is required');
      }
      return await getDocumentDownloadUrl(documentId);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isGetDocumentDownloadUrlLoading,
        isGetDocumentDownloadUrlFetching,
        isGetDocumentDownloadUrlError,
        getDocumentDownloadUrlError,
        isGetDocumentDownloadUrlSuccess,
        getDocumentDownloadUrl: handleGetDocumentDownloadUrl,
      }),
      [
        isGetDocumentDownloadUrlLoading,
        isGetDocumentDownloadUrlFetching,
        isGetDocumentDownloadUrlError,
        getDocumentDownloadUrlError,
        isGetDocumentDownloadUrlSuccess,
        handleGetDocumentDownloadUrl,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error getting document download URL:', error);
    throw error;
  }
};

export const useGetUserDocuments = () => {
  try {
    // states
    const [data, setData] = useState([]);
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getUserDocuments,
      isLoading: isGetUserDocumentsLoading,
      isError: isGetUserDocumentsError,
      error: getUserDocumentsError,
      isFetching: isGetUserDocumentsFetching,
      refetch: refetchGetUserDocuments,
    } = useGetUserDocumentsQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetUserDocuments = async (
      page,
      limit,
      documentType,
      status
    ) => {
      setSkipApi(false);
      // Filter out null/undefined values from query before sending to API
      const cleanQuery = Object.fromEntries(
        Object.entries({ page, limit, documentType, status }).filter(
          ([_, value]) => value !== null && value !== undefined && value !== ''
        )
      );
      setQuery(cleanQuery);
    };

    // effect
    useEffect(() => {
      if (getUserDocuments !== undefined) {
        setData(getUserDocuments.data);
        setSkipApi(true);
      }
    }, [getUserDocuments, skipApi]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetUserDocumentsLoading,
        isGetUserDocumentsError,
        getUserDocumentsError,
        isGetUserDocumentsFetching,
        refetchGetUserDocuments,
        getUserDocuments: handleGetUserDocuments,
      }),
      [
        data,
        isGetUserDocumentsLoading,
        isGetUserDocumentsError,
        getUserDocumentsError,
        isGetUserDocumentsFetching,
        refetchGetUserDocuments,
        handleGetUserDocuments,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching user documents:', error);
    throw error;
  }
};

export const useGetTaxFileDocuments = () => {
  try {
    // states
    const [data, setData] = useState([]);
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({
      taxFileId: '',
      questionId: '',
    });

    // api call query
    const {
      data: getTaxFileDocuments,
      isLoading: isGetTaxFileDocumentsLoading,
      isError: isGetTaxFileDocumentsError,
      error: getTaxFileDocumentsError,
      isFetching: isGetTaxFileDocumentsFetching,
      refetch: refetchGetTaxFileDocuments,
    } = useGetTaxFileDocumentsQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetTaxFileDocuments = async query => {
      if (!query.taxFileId) {
        throw new Error('Tax file ID is required');
      }
      setQuery(query);
      setSkipApi(false);
    };

    // effect
    useEffect(() => {
      if (getTaxFileDocuments !== undefined && getTaxFileDocuments.data) {
        setData(getTaxFileDocuments.data);
        setSkipApi(true); // Reset skipApi after data is set to prevent unnecessary re-fetches
      }
    }, [getTaxFileDocuments, setSkipApi]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetTaxFileDocumentsLoading,
        isGetTaxFileDocumentsError,
        getTaxFileDocumentsError,
        isGetTaxFileDocumentsFetching,
        refetchGetTaxFileDocuments,
        getTaxFileDocuments: handleGetTaxFileDocuments,
      }),
      [
        data,
        isGetTaxFileDocumentsLoading,
        isGetTaxFileDocumentsError,
        getTaxFileDocumentsError,
        isGetTaxFileDocumentsFetching,
        refetchGetTaxFileDocuments,
        handleGetTaxFileDocuments,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching documents by tax file:', error);
    throw error;
  }
};

export const useDeleteDocument = () => {
  try {
    // api call mutation
    const [
      deleteDocument,
      {
        isLoading: isDeleteDocumentLoading,
        isError: isDeleteDocumentError,
        error: deleteDocumentError,
        isSuccess: isDeleteDocumentSuccess,
        isFetching: isDeleteDocumentFetching,
      },
    ] = useDeleteDocumentMutation();

    // handlers
    const handleDeleteDocument = async documentId => {
      if (!documentId) {
        throw new Error('Document ID is required');
      }
      return await deleteDocument(documentId);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isDeleteDocumentLoading,
        isDeleteDocumentFetching,
        isDeleteDocumentError,
        deleteDocumentError,
        isDeleteDocumentSuccess,
        deleteDocument: handleDeleteDocument,
      }),
      [
        isDeleteDocumentLoading,
        isDeleteDocumentFetching,
        isDeleteDocumentError,
        deleteDocumentError,
        isDeleteDocumentSuccess,
        handleDeleteDocument,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

export const useUpdateTaxFileDocuments = () => {
  try {
    // api call mutation
    const [
      updateTaxFileDocuments,
      {
        isLoading: isUpdateTaxFileDocumentsLoading,
        isError: isUpdateTaxFileDocumentsError,
        error: updateTaxFileDocumentsError,
        isSuccess: isUpdateTaxFileDocumentsSuccess,
      },
    ] = useUpdateTaxFileDocumentsMutation();

    // handlers
    const handleUpdateTaxFileDocuments = async (
      taxFileId,
      documentUpdates,
      documentAdditions
    ) => {
      if (!taxFileId) {
        throw new Error('Tax file ID is required');
      }
      return await updateTaxFileDocuments({
        taxFileId,
        documentUpdates,
        documentAdditions,
      });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUpdateTaxFileDocumentsLoading,
        isUpdateTaxFileDocumentsError,
        updateTaxFileDocumentsError,
        isUpdateTaxFileDocumentsSuccess,
        updateTaxFileDocuments: handleUpdateTaxFileDocuments,
      }),
      [
        isUpdateTaxFileDocumentsLoading,
        isUpdateTaxFileDocumentsError,
        updateTaxFileDocumentsError,
        isUpdateTaxFileDocumentsSuccess,
        handleUpdateTaxFileDocuments,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error updating tax file documents:', error);
    throw error;
  }
};
